import React, { FunctionComponent, useState } from 'react';

import { useEvent } from 'effector-react/ssr';

import clsx from 'clsx';
import { RemoveScroll } from 'react-remove-scroll';

import { CategoriesTree, CategoryTreeRootItem } from '@domains/categories';

import { setIsOpenCatalogMenu } from '@stores/header';

import { isValueSet } from '@utils/guards';
import { makeCategoryRoute } from '@utils/route';

import { Container } from '@components/common/Container/Container';
import { Link } from '@components/common/Link/Link';
import { Picture } from '@components/common/Picture/Picture';
import { ArrowRightIcon } from '@components/icons';

import styles from './CatalogWidget.module.scss';

export type CatalogWidgetProps = {
    categories: CategoriesTree;
    isOpen: boolean;
    className?: string;
};

const categoryItem = (name: string, isChevronShown: boolean) => (
    <>
        {name}
        {isChevronShown && <ArrowRightIcon className={styles.chevronIcon} />}
    </>
);

export const CatalogWidget: FunctionComponent<CatalogWidgetProps> = (props) => {
    const { categories, className, isOpen = false } = props;

    const setIsOpenCatalogMenuFn = useEvent(setIsOpenCatalogMenu);

    const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);

    const hasCategories = categories.length > 0;
    const selectedCategory: CategoryTreeRootItem | undefined = hasCategories
        ? categories[selectedCategoryIndex]
        : undefined;

    const handleLinkClick = () => {
        setIsOpenCatalogMenuFn(false);
    };
    return (
        <RemoveScroll
            className={clsx(styles.root, isOpen && styles.isOpen, className)}
            enabled={isOpen}
            noIsolation
        >
            <Container classes={{ root: styles.container }}>
                <div className={styles.inner}>
                    <ul className={styles.categories}>
                        {categories.map(({ name, id, thumbnail, slug, subcategories }, index) => (
                            <li
                                onMouseEnter={() => setSelectedCategoryIndex(index)}
                                className={styles.category}
                                key={id}
                            >
                                <Link
                                    className={styles.categoryLink}
                                    route={makeCategoryRoute(slug)}
                                    onClick={handleLinkClick}
                                >
                                    {thumbnail !== null && (
                                        <Picture
                                            sources={thumbnail}
                                            className={styles.image}
                                            classes={{
                                                picture: styles.picture,
                                            }}
                                        />
                                    )}
                                    <span>{categoryItem(name, subcategories.length > 0)}</span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                    <div className={styles.categoryInner}>
                        <ul className={styles.subcategories}>
                            {isValueSet(selectedCategory) && (
                                <>
                                    <li className={styles.subcategory}>
                                        <Link
                                            route={makeCategoryRoute(selectedCategory.slug)}
                                            className={clsx(
                                                styles.subcategoryLink,
                                                styles.allCategories,
                                            )}
                                            onClick={handleLinkClick}
                                        >
                                            Все товары категории
                                        </Link>
                                    </li>
                                    {selectedCategory.subcategories.length > 0 &&
                                        selectedCategory.subcategories.map(
                                            ({ slug, name, thumbnail }) => (
                                                <li className={styles.subcategory} key={slug}>
                                                    <Link
                                                        route={makeCategoryRoute(slug)}
                                                        className={styles.subcategoryLink}
                                                        onClick={handleLinkClick}
                                                    >
                                                        {thumbnail !== null && (
                                                            <Picture
                                                                sources={thumbnail}
                                                                className={styles.subcategoryImage}
                                                                classes={{
                                                                    picture: styles.picture,
                                                                }}
                                                            />
                                                        )}
                                                        {name}
                                                    </Link>
                                                </li>
                                            ),
                                        )}
                                </>
                            )}
                        </ul>
                    </div>
                </div>
            </Container>
        </RemoveScroll>
    );
};
